import {
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import {
  Configuration, ConfigurationState,
  InspectionDamageDto,
  InspectionImageDto,
  InspectionReportDto,
  PostingDataResponseModel,
  EditPostingState,
  UpdatePosting,
  EditPostingFormNames,
  LaunchDarklyService
} from 'remarketing-angular-library';
import {Store} from '@ngxs/store';
import {AccordionModule, AccordionPanelComponent} from 'ngx-bootstrap/accordion';
import {takeUntil} from 'rxjs/operators';
import {cloneDeep, get} from 'lodash';
import {CustomValidatorConfig, PhotoUploadService} from '../../services/photo-upload.service';
import {ExistingPhotosModalComponent} from '../existing-photos-modal/existing-photos-modal.component';
import {PostingFormDirective} from '../posting-form/posting-form.directive';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { CurrencyFormatService, NameTruncatePipe } from 'smartauction-frontend-micro-library';
import { TranslocoService } from '@jsverse/transloco';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import {
  ImagesCountComponent
} from '../../../sell-shared/components/images-count/images-count.component';
import { NgxMaskModule } from 'ngx-mask';
import { PhotosCardComponent } from '../photos-card/photos-card.component';
import { CommonModule } from '@angular/common';


@Component({
    selector: 'app-damages-form',
    templateUrl: './damages-form.component.html',
    styleUrls: ['./damages-form.component.scss'],
    imports: [
        AccordionModule,
        ExistingPhotosModalComponent,
        ImagesCountComponent,
        ReactiveFormsModule,
        NgxMaskModule,
        PhotosCardComponent,
        CommonModule,
        NameTruncatePipe
    ]
})
export class DamagesFormComponent extends PostingFormDirective implements OnInit {
  @ViewChild(ExistingPhotosModalComponent) existingPhotosModal: ExistingPhotosModalComponent;
  @Input() postingTypeString: string;
  originalPostingData: PostingDataResponseModel;
  postingData: PostingDataResponseModel;
  existingPhotoList: InspectionImageDto[];
  damageForms: UntypedFormGroup[] = [];
  damageTypeOptions: string[] = [];
  addDamageForm: UntypedFormGroup;
  openFormIndex = null;
  isAddDamageOpen = false;
  addDamagePanelClass = 'add-damage-information';
  vehicleVINNumber: string;
  defaultPhotoCount = 5;
  damageFormCustomValidator: CustomValidatorConfig = {
    validatorName: 'validImageCount',
    validatorValue: 5,
    validatorErrMsgName: 'PHOTO_COUNT_ERROR',
    validatorErrMsgValue: "You can't upload more than five images."
  };
  errMsg: string;
  newDmgFormIndex: number;
  defaultOptions = {
    type: null,
    location: '',
    description: '',
    chargeableFlag: false,
    total: null,
    exceptionImages: [],
    totalErrors: 0
  };
  damagesFormTranslations: any;
  commonTranslations: any;

  configuration: Configuration;

  @ViewChildren(AccordionPanelComponent) accordionPanels: QueryList<AccordionPanelComponent>;

  constructor(
    private readonly store: Store,
    public readonly formBuilder: UntypedFormBuilder,
    public readonly photoUploadService: PhotoUploadService,
    public readonly editPostingUtilsService: EditPostingUtilsService,
    public readonly changeDetector: ChangeDetectorRef,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly currencyFormatService: CurrencyFormatService,
    private readonly translocoService: TranslocoService,
    private readonly launchDarklyService: LaunchDarklyService
  ) {
    super();
  }

  get isCanadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }
  get isFrenchCanadian(): boolean {
    const frenchLanguage = this.translocoService.getActiveLang().includes('fr');
    return frenchLanguage && this.isCanadianTenantEnabled;
  }

  ngOnInit() {
    this.getTranslations();
    this.store
      .select(state => state.configuration.configuration)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(configuration => (this.configuration = configuration));

    // Get our posting data
    this.originalPostingData = cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
      });
    this.vehicleVINNumber = this.postingData.inventoryItem.vehicleInformation.vin;

    // Initialize our exceptions array if it doesn't exist
    if (!this.postingData.inventoryItem.inspectionReport?.inspectionData?.exceptions) {
      const inspectionReport = this.postingData.inventoryItem.inspectionReport || ({} as InspectionReportDto);
      this.postingData.inventoryItem.inspectionReport = {
        ...inspectionReport,
        inspectionData: {
          ...inspectionReport?.inspectionData,
          exceptions: []
        }
      };
    }

    // Populate our FormGroup array
    this.postingData.inventoryItem?.inspectionReport?.inspectionData?.exceptions?.forEach((x, index) => {
      const images = x.exceptionImages ? x.exceptionImages : [];
      const damageForm = this.formBuilder.group({
        type: x.type,
        location: x.location,
        description: x.description,
        chargeableFlag: x.chargeableFlag,
        total: x.total,
        exceptionImages: this.formBuilder.array([...images]),
        totalErrors: 0
      });
      this.handleFormValidators(damageForm);
      this.handleTotalField(damageForm);

      this.damageForms.push(damageForm);
    });

    // Damage types are hardcoded until available via the API
    this.damageTypeOptions = ['Exterior', 'Interior', 'Other'];
    this.existingPhotoList = this.editPostingUtilsService.constructExistingPhotosList();
    this.getUpdatesFromPhotoService();
    this.sendPageloadAnalyticsEvent();
  }

  formatCurrency(value: number): string {
    return this.currencyFormatService.formatCurrency(value, true);
  }

  handleDisabledAndErrorStyling(damageForm, error) {
    return damageForm.get('total').disabled ? 'currency-input__disabled' : error ? 'error-border' : '';
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.DISCLOSURES_DAMAGES
    );
  }

  updateDamage(index) {
    const damage = this.postingData.inventoryItem.inspectionReport.inspectionData.exceptions[index];
    const images = damage.exceptionImages ? damage.exceptionImages : [];
    const form = this.damageForms[index];

    this.processErrors(form);

    // Only update the damages if there are no errors
    if (form.get('totalErrors').value === 0) {
      damage.type = form.get('type').value;
      damage.location = form.get('location').value;
      damage.description = form.get('description').value;
      damage.chargeableFlag = form.get('chargeableFlag').value;
      damage.total = damage.chargeableFlag ? parseFloat(form.get('total').value) : 0;
      damage.exceptionImages = [...images];
      this.store.dispatch(new UpdatePosting(this.postingData));
      this.clickAccordion(index);

      // Take new data into account for Add/Save button consistency
      this.originalPostingData = cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));
      this.postingData = cloneDeep(this.store.selectSnapshot(EditPostingState.updatedPosting));
    }
  }

  cancelEditDamage(index, isCancel = false) {
    const damage = this.postingData.inventoryItem.inspectionReport.inspectionData.exceptions[index];
    const images = damage.exceptionImages ? damage.exceptionImages : [];

    // Reset our form to its initial state
    this.damageForms[index].patchValue({
      type: damage.type,
      location: damage.location,
      description: damage.description,
      chargeableFlag: damage.chargeableFlag,
      total: damage.total,
      exceptionImages: [...images],
      totalErrors: 0
    });

    // Focus on the edit button if we've hit the Cancel button
    if (isCancel) {
      const editButton = document.getElementById(`edit-damage-${index}`);
      editButton.focus();
    }
  }

  clickAccordion(index, isCancel = false) {
    const panelClass = 'edit-information-' + index;
    const openAccordion = this.accordionPanels.find(acc => acc.isOpen);
    const accordion = this.accordionPanels.find(acc => acc.panelClass === panelClass);

    //Clear open index
    this.openFormIndex = null;
    // Nothing open yet, or closing an already open accordion
    if (!openAccordion || (openAccordion && openAccordion.panelClass === panelClass)) {
      accordion.isOpen = !accordion.isOpen;
      // Clear out unsaved changes to open accordion
      if (openAccordion) {
        this.cancelEditDamage(index, isCancel);
      } else {
        this.openFormIndex = index;
      }
    }
    // Opening a new accordion while one is already open
    if (openAccordion && openAccordion.panelClass !== panelClass) {
      openAccordion.isOpen = false;
      accordion.isOpen = true;
      const openIndex = this.accordionPanels.toArray().indexOf(openAccordion);
      if (openAccordion.panelClass !== this.addDamagePanelClass) {
        this.cancelEditDamage(openIndex, isCancel);
      }
      this.openFormIndex = index;
    }

    // Handle validation
    this.handleFormValidators(this.damageForms[index]);
    this.handleTotalField(this.damageForms[index]);
  }

  removeDamage(index) {
    this.damageForms.splice(index, 1);
    this.postingData.inventoryItem.inspectionReport.inspectionData.exceptions.splice(index, 1);
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  handleTotalField(damage: UntypedFormGroup) {
    const chargeableFlag = damage.controls.chargeableFlag.value;

    // The chargeableFlag state determines if the total field is required
    if (!chargeableFlag) {
      damage.patchValue({
        total: null
      });
      damage.controls.total.removeValidators(Validators.required);
      damage.controls.total.disable();
    } else {
      damage.controls.total.addValidators(Validators.required);
      damage.controls.total.enable();
    }
  }

  handleFormValidators(damageForm: UntypedFormGroup) {
    // Handle validation
    damageForm.controls.type.addValidators(Validators.required);
    damageForm.controls.location.addValidators(Validators.required);
    damageForm.controls.description.addValidators(Validators.required);
    if (damageForm.controls.chargeableFlag.value) {
      damageForm.controls.total.addValidators(Validators.required);
      damageForm.controls.total.addValidators(Validators.min(0.01));
    }
  }

  processErrors(damageForm: UntypedFormGroup) {
    let totalErrors = 0;
    Object.keys(damageForm.controls).forEach(x => {
      const control = damageForm.controls[x];
      if (control.invalid) {
        totalErrors++;
      }
    });

    damageForm.patchValue({ totalErrors: totalErrors });
  }

  errorText(damage: UntypedFormGroup) {
    if (damage.get('totalErrors').value > 1) {
      return `There are ${damage.get('totalErrors').value} errors in the form you submitted.`;
    } else {
      return `There is ${damage.get('totalErrors').value} error in the form you submitted.`;
    }
  }

  showError(index: number, field: string) {
    return this.damageForms[index].get(field).invalid && this.damageForms[index].get('totalErrors').value > 0;
  }

  showAddDamageFormError(field: string) {
    return this.addDamageForm.get(field).invalid && this.addDamageForm.get('totalErrors').value > 0;
  }

  get damages(): InspectionDamageDto[] {
    return get(this.postingData.inventoryItem.inspectionReport.inspectionData, 'exceptions', []);
  }

  get originalDamages(): InspectionDamageDto[] {
    return get(this.originalPostingData.inventoryItem.inspectionReport.inspectionData, 'exceptions', []);
  }

  isMobile(): boolean {
    return window.matchMedia('(max-width: 768px)').matches;
  }

  addDamageChangeEvent(event: boolean) {
    if (event) {
      this.addDamageForm = this.formBuilder.group(this.defaultOptions);
      this.handleFormValidators(this.addDamageForm);
      this.handleTotalField(this.addDamageForm);
      this.openFormIndex = null;
      this.newDmgFormIndex = this.damages.length;
    }
  }

  updateNewDamage(currIndex) {
    this.processErrors(this.addDamageForm);
    // Update the damages with new addDamageForm if there are no errors
    if (this.addDamageForm.get('totalErrors').value === 0) {
      this.setNewDamageData(currIndex);
      this.store.dispatch(new UpdatePosting(this.postingData));
      if (!this.damageForms[currIndex]) {
        this.damageForms.push(this.addDamageForm);
      }

      // Take new data into account for Add/Save button consistency
      this.originalPostingData = cloneDeep(this.store.selectSnapshot(EditPostingState.originalPosting));
      this.postingData = cloneDeep(this.store.selectSnapshot(EditPostingState.updatedPosting));
      this.closeAddDamageForm();
    }
  }

  setNewDamageData(currIndex) {
    const imageList = this.getDamageImages(currIndex);
    const damage = {
      type: this.addDamageForm.get('type').value,
      location: this.addDamageForm.get('location').value,
      description: this.addDamageForm.get('description').value,
      chargeableFlag: this.addDamageForm.get('chargeableFlag').value,
      total: this.addDamageForm.get('chargeableFlag').value ? this.addDamageForm.get('total').value : 0,
      exceptionImages: imageList
    };
    if (this.damages && this.damages[currIndex]) {
      this.postingData.inventoryItem.inspectionReport.inspectionData.exceptions[currIndex] = damage;
    } else {
      this.postingData.inventoryItem.inspectionReport.inspectionData.exceptions.push(damage);
    }
  }

  closeAddDamageForm() {
    const openAccordion = this.accordionPanels.find(acc => acc.isOpen);
    openAccordion.isOpen = false;
    this.isAddDamageOpen = !this.isAddDamageOpen;
  }

  /**
   *
   * @param actionName - action from action bar component
   * its deletes the selected card
   */
  actionBarUpdatesFromPhotosCard(data) {
    const { actionName, currIndex } = data;
    const dmgImages = this.damages[currIndex].exceptionImages;
    const filteredArry =
      actionName === 'delete'
        ? dmgImages.filter((item: any) => {
            return !item.isSelected && !item.showSpinner;
          })
        : dmgImages.map((item: any) => {
            delete item.isSelected;
            return item;
          });
    this.damages[currIndex].exceptionImages = filteredArry;
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  /**
   *
   * @param currIndex - index the damage form
   * sets the custom validator and show file explorer to select photos
   */
  selectPhotos(currIndex) {
    const dmgImages = this.getDamageImages(currIndex);
    const currentListLength = dmgImages.length;
    this.damageFormCustomValidator.validatorValue = this.defaultPhotoCount - currentListLength;
    this.photoUploadService.selectPhotos(this.vehicleVINNumber, this.damageFormCustomValidator, currIndex);
  }

  //Subscribe subjects from photo upload service
  getUpdatesFromPhotoService() {
    //gets VIW response from photo upload service
    this.photoUploadService.photoUploadResponseData.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
      const { response, currIndex } = data;
      const dmgImages = this.getDamageImages(currIndex);
      if (response && response.showSpinner) {
        dmgImages.push(...response.spinnerList);
        this.damages[currIndex].exceptionImages = dmgImages;
      } else {
        this.updateDamageImageList(response, dmgImages, currIndex);
      }
      delete (this.damages[currIndex] as any).showErrMsg;
    });

    //gets error status from photo upload service
    this.photoUploadService.photoUploadErrStatus.pipe(takeUntil(this.destroyed$)).subscribe((data: any) => {
      const { errMsg, currIndex } = data;
      this.errMsg = errMsg;
      if (this.errMsg) {
        (this.damages[currIndex] as any).showErrMsg = true;
        this.damages[currIndex].exceptionImages = this.damages[currIndex].exceptionImages.filter((item: any) => {
          return !item.showSpinner;
        });
      }
    });
  }

  //updates the newly uploaded image in the existing image list
  updateDamageImageList(imageDetails, dmgImages: InspectionImageDto[], currIndex) {
    const findSpinnerIndex = dmgImages.findIndex((imgItem: any) => {
      return imgItem.showSpinner;
    });
    const imgObj = {
      sourceUrl: imageDetails.files[0].fullSizedUrl
    };
    dmgImages.splice(findSpinnerIndex, 1, imgObj);
    this.damages[currIndex].exceptionImages = cloneDeep(dmgImages);
    if (this.isAddDamageOpen && !this.damageForms[currIndex]) {
      this.damageForms.push(this.addDamageForm);
    }
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.changeDetector.detectChanges();
  }

  /**
   *
   * @param currIndex - index of the selected card
   * Construct the all image list and shows the existing modal
   */
  showExistingModal(currIndex) {
    const existingInspectionImgList = this.editPostingUtilsService.constructExistingPhotosList();
    const dmgImages = this.getDamageImages(currIndex);
    existingInspectionImgList.forEach((imageItem: any) => {
      const inspectionUrl = imageItem.url || imageItem.sourceUrl;
      dmgImages.forEach(dmgItem => {
        const dmgUrl = dmgItem.url || dmgItem.sourceUrl;
        if (inspectionUrl === dmgUrl) {
          imageItem.isExistingImage = true;
        }
      });
    });
    this.existingPhotoList = existingInspectionImgList;
    this.changeDetector.detectChanges();
    this.existingPhotosModal.show(currIndex);
    this.postingAnalyticsHelperService.trackModalView('existingPhotos');
  }

  /**
   *
   * @param photoList - Image list from existing photo modal Component
   * Updates the latest image in the UI and save the date in store
   */
  photosFromExistingModal(modalUpdates) {
    const { photoList, currIndex } = modalUpdates;
    const selectedImages = [];
    const currDmgLocation = this.damages[currIndex].location;
    photoList.forEach((item: any) => {
      if (item.isExistingImage) {
        //if image is added from existing modal changing its location to current damage location
        item.location = currDmgLocation;
        item.damageIndex = currIndex;
        const { isExistingImage, ...rest } = item;
        selectedImages.push({ url: rest.url || rest.sourceUrl });
      } else {
        //Needs to change the location if other damage form image is unselected using existing modal
        if (item.damageIndex === currIndex) {
          item.location = '';
          delete item.damageIndex;
        }
      }
      delete item.isExistingImage;
    });
    this.damages[currIndex].exceptionImages = cloneDeep(selectedImages);
    delete (this.damages[currIndex] as any).showErrMsg;
    if (this.isAddDamageOpen && !this.damageForms[currIndex]) {
      this.damageForms.push(this.addDamageForm);
    }
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.editPostingUtilsService.updateInspectionImageData(photoList);
    this.changeDetector.detectChanges();
  }

  /**
   *
   * @param currIndex - index of the current exception
   * @returns gets respective index exception images
   */
  getDamageImages(currIndex: number): any {
    return (this.damages[currIndex] && this.damages[currIndex].exceptionImages) || [];
  }

  /**
   *
   * @param isExistingUpload - boolean to check the flow to call
   * @param currIndex - index of the current exception
   * Check whether form is valid or not and call respective function
   */
  checkForNewDamageIsValid(isExistingUpload, currIndex) {
    this.processErrors(this.addDamageForm);
    if (this.addDamageForm.get('totalErrors').value === 0) {
      this.setNewDamageData(currIndex);
      if (isExistingUpload) {
        this.showExistingModal(currIndex);
      } else {
        this.selectPhotos(currIndex);
      }
    }
  }

  getTranslations() {
    const keyParams = {
      'sellModule.posting.damagesForm': {},
      common: {}
    };

    // > 80 translations for 'damagesForm,' done as object for ease of reference in DOM
    this.translocoService
      .selectTranslateObject(keyParams)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.damagesFormTranslations = translate[0];
        this.commonTranslations = translate[1];
      });
  }
}
